import {
  TrialRequestStatus,
  useGetTrialRequestQuery,
} from "@/__generated__/graphql";
import { useUpdateTrialRequest } from "@/app/form/data";
import { WidgetAPI } from "@/context/widget-context";
import { cn, Language, useTranslations } from "@/lib";
import { ApolloError } from "@apollo/client";
import { addMinutes, format, parseISO } from "date-fns";
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { ScheduleFormData } from "../../app/form/schema";
import { Button } from "../ui";
import {
  CalendarIcon,
  ClockIcon,
  EditIcon,
  EmailIcon,
  LocationIcon,
  PhoneIcon,
} from "../ui/icons";
import Spinner from "../ui/spinner";
import { ProductItem } from "./products/product-item";
export function Review({
  lang,
  widget,
  form,
}: {
  lang: Language;
  widget: WidgetAPI;
  form: UseFormReturn<ScheduleFormData>;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslations(lang);
  const { loading: fetchLoading } = useGetTrialRequestQuery({
    variables: {
      id: widget.trialRequestId,
    },
    onCompleted: (data) => {
      form.reset({
        emirate: form.watch("emirate"),
        region: data.trialRequest?.region || "",
        date: new Date(data.trialRequest?.startDateTime),
        startDateTime: data.trialRequest?.startDateTime,
        duration: data.trialRequest?.duration || undefined,
        name: data.trialRequest?.customer.name,
        phone: data.trialRequest?.customer.phone,
        email: data.trialRequest?.customer.email || "",
        address: data.trialRequest?.customer?.address || "",
      });
    },
    skip: !widget.trialRequestId,
  });
  const updateTrialRequestMutation = useUpdateTrialRequest({
    onCompleted: (data) => {
      widget.setStep(7);
      widget.setTrialRequestId(null);
      widget.removeAllProducts();
    },
  });
  async function onSubmit() {
    setLoading(true);
    try {
      const trialRequestId = widget.trialRequestId;
      if (!trialRequestId) {
        throw new Error("Invalid Trial request, Please Start Over");
      }
      await updateTrialRequestMutation(String(trialRequestId), {
        status: TrialRequestStatus.Completed,
      });
    } catch (error) {
      let message = "Something went wrong. Please try again.";
      if (error instanceof ApolloError) {
        message =
          error.graphQLErrors[0]?.message ||
          error.networkError?.message ||
          message;
      } else if (error instanceof Error) {
        message = error.message;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return fetchLoading ? (
    <Spinner className="tw:h-10 tw:w-10 tw:mx-auto" />
  ) : (
    <div className="tw:relative">
      <div className="tw:grid tw:gap-5 tw:px-4 tw:pb-8 tw:!border-b">
        <span>
          {`${t("review.text_one")}${
            widget.products.filter((product) => product.size).length > 1
              ? "s"
              : ""
          }`}
        </span>

        {widget.products
          .filter((product) => product.size)
          .map((product, index) => (
            <ProductItem key={index} product={product} lang={lang} />
          ))}

        {widget.products.some((product) => !product.size) && (
          <>
            <span>{t("review.text_two")}</span>
            {widget.products
              .filter((product) => !product.size)
              .map((product, index) => (
                <ProductItem key={index} product={product} lang={lang} />
              ))}
          </>
        )}
      </div>

      <div className="tw:px-4 tw:py-8 tw:!border-b">
        <div
          className={cn(
            "tw:font-semibold tw:flex tw:items-center",
            lang === "ar" && "tw:justify-end",
          )}
        >
          {t("review.text_three")}
          <Button
            variant="ghost"
            className="tw:!ml-3 tw:!p-0 tw:!h-4"
            onClick={() => widget.setStep(5)}
          >
            <EditIcon />
          </Button>
        </div>

        <div className="tw:mt-2">
          <div
            className={cn(
              "tw:font-medium tw:mb-2",
              lang === "ar" ? "tw:flex tw:flex-row-reverse" : "",
            )}
          >
            <CalendarIcon
              className={cn(
                "tw:!inline",
                lang === "ar" ? "tw:ml-2" : "tw:mr-2",
              )}
            />
            {t("review.date_text")}:{" "}
            <span
              className={cn(
                "tw:font-semibold",
                lang === "ar" ? "tw:!mr-2" : "tw:!ml-2",
              )}
              dir="ltr"
            >
              {form.getValues("date")
                ? format(form.getValues("date"), "EEEE, do MMMM yyyy")
                : "-"}
            </span>
          </div>

          <div
            className={cn(
              "tw:font-medium tw:mb-2",
              lang === "ar" ? "tw:flex tw:flex-row-reverse" : "",
            )}
          >
            <ClockIcon
              className={cn(
                "tw:!inline",
                lang === "ar" ? "tw:ml-2" : "tw:mr-2",
              )}
            />
            {t("review.time_text")}:{" "}
            <span
              className={cn(
                "tw:font-semibold",
                lang === "ar" ? "tw:!mr-2" : "tw:!ml-2",
              )}
              dir="ltr"
            >
              {form.getValues("startDateTime")
                ? `${format(parseISO(String(form.getValues("startDateTime"))), "hh:mm aa")} - ${format(addMinutes(parseISO(String(form.getValues("startDateTime"))), Number(form.getValues("duration"))), "hh:mm aa")}`
                : "-"}
            </span>
          </div>
        </div>
      </div>

      <div className="tw:px-4 tw:py-8 tw:mb-40 tw:md:mb-0">
        <div
          className={cn(
            "tw:font-semibold tw:flex tw:items-center",
            lang === "ar" && "tw:justify-end",
          )}
        >
          {t("review.contact_details_text")}
          <Button
            variant="ghost"
            className="tw:!ml-3 tw:!p-0 tw:!h-4"
            onClick={() => widget.setStep(5)}
          >
            <EditIcon />
          </Button>
        </div>

        <div className="tw:mt-3 tw:flex tw:flex-col tw:gap-3">
          <div className="tw:font-semibold">{form.getValues("name")}</div>

          <div
            className={cn(
              "tw:font-medium",
              lang === "ar"
                ? "tw:flex tw:flex-row-reverse tw:items-center tw:gap-2 tw:[&_svg]:mr-0"
                : "",
            )}
          >
            <EmailIcon className="tw:!inline tw:mr-2" />
            {form.getValues("email")}
          </div>

          <div
            className={cn(
              "tw:font-medium",
              lang === "ar"
                ? "tw:flex tw:flex-row-reverse tw:items-center tw:gap-2 tw:[&_svg]:mr-0"
                : "",
            )}
          >
            <PhoneIcon className="tw:!inline tw:mr-2" />
            {form.getValues("phone")}
          </div>

          <div
            className={cn(
              "tw:font-medium tw:mr-40 tw:flex tw:items-start",
              lang === "ar"
                ? "tw:flex-row-reverse tw:mr-0 tw:gap-2 tw:[&_svg]:mr-0"
                : "",
            )}
          >
            <div>
              <LocationIcon className="tw:mr-2" />
            </div>
            <div className="tw:whitespace-pre-line">
              {form.getValues("address")}
            </div>
          </div>
        </div>
      </div>

      <div className="tw:fixed tw:bottom-0 tw:bg-white tw:w-[95%] tw:md:w-lg hide-footer--btn tw:z-100">
        <div className="tw:text-center tw:!border-t">
          {error ? (
            <div className="tw:text-destructive tw:text-sm tw:font-medium tw:text-center tw:mb-2">
              {error}
            </div>
          ) : null}
          <Button
            id="fht--confirm"
            onClick={onSubmit}
            className="tw:md:!px-32 tw:!px-20 tw:!z-100 tw:!text-sm tw:md:!text-base tw:w-full tw:!h-13.5"
            disabled={loading}
          >
            {t("review.confirm_and_continue")}
            {loading && <Spinner className="tw:h-5" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
